import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { appService } from '../../_services/app.service';
import {
  NoteViewModel,
  CreateNoteViewModel,
  UpdateNoteViewModel,
  AddAttachmentViewModel,
  MentionViewModel,
  TrashedNoteViewModel,
  SearchNoteResultViewModel,
  NoteFilterParams,
  GlobalSearchParams,
  GlobalNoteFilterParams,
  GlobalNoteCounts,
  UserTag,
  TrashedNoteGlobalViewModel
} from '../models/note.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
//import { CookieService } from 'ngx-cookie-service';
//import { DecryptPipe } from '../../auth/_services/encrypt-decrypt.pipe';

@Injectable()
export class NotesService {

  private _notesRefresh$ = new Subject<void>();
  private _unreadMentionCount$ = new BehaviorSubject<number>(0);

  get notesRefresh$(): Observable<void> {
    return this._notesRefresh$;
  }

  get unreadMentionCount$(): Observable<number> {
    return this._unreadMentionCount$;
  }

  constructor(
    private appService: appService,
    private http: HttpClient,
  //  private cookieService: CookieService,
    //private decryptpipe: DecryptPipe
  ) {}

  private buildHeaders(): HttpHeaders {
    const companyData = JSON.parse(localStorage.getItem("ChnageCompnayId"));

    const token = this.appService.getAuthToken();

    return new HttpHeaders({ Authorization: token, CompanyId: companyData ? companyData.Id : ''});
  }

  triggerRefresh(): void {
    this._notesRefresh$.next();
  }


  getNotesByTransaction(params: NoteFilterParams): Observable<any> {
    let queryParams = [];
    queryParams.push(`transactionType=${encodeURIComponent(params.TransactionType)}`);
    queryParams.push(`transactionId=${params.TransactionId}`);
    queryParams.push(`pageNumber=${params.PageNumber}`);
    queryParams.push(`pageSize=${params.PageSize}`);
    
    if (params.SearchTerm) queryParams.push(`searchTerm=${encodeURIComponent(params.SearchTerm)}`);
    if (params.NoteType) queryParams.push(`noteType=${encodeURIComponent(params.NoteType)}`);
    if (params.IsPinned != null) queryParams.push(`isPinned=${params.IsPinned}`);
    if (params.IsArchived != null) queryParams.push(`isArchived=${params.IsArchived}`);
    if (params.HasReminder != null) queryParams.push(`hasReminder=${params.HasReminder}`);
    
    const queryString = queryParams.length > 0 ? '?' + queryParams.join('&') : '';
    const url = 'GetNotes' + queryString;
    
    console.log('Calling Notes API:', url);  

    return this.http.get(`${environment.apiUrl}api/Notes/${url}`,
      { headers: this.buildHeaders()}
    ).pipe(
      map((res: any) => {
        console.log('API Response:', res);

        if (res && res.notes && Array.isArray(res.notes)) {
          res.notes = res.notes.map((note: any) => {
            if (note.AttachmentsJson && typeof note.AttachmentsJson === 'string') {
              try {
                note.Attachments = JSON.parse(note.AttachmentsJson);
                console.log(`Parsed ${note.Attachments.length} attachments for note ${note.NoteId}`);
              } catch (e) {
                console.error('Error parsing AttachmentsJson', e);
                note.Attachments = [];
              }
            } else {
              note.Attachments = [];
            }

            if (note.MentionsJson && typeof note.MentionsJson === 'string') {
              try {
                note.Mentions = JSON.parse(note.MentionsJson);
              } catch (e) {
                console.error("Error parsing MentionsJson:", e);
                note.Mentions = [];
              }
            } else {
              note.Mentions = [];
            }

            return note;
          });
        }
        return res;
      })
    );
  }

  deleteAttachment(attachmentId: string): Observable<any> {
    return this.appService.Post('Notes', `DeleteAttachment?attachmentId=${attachmentId}`, {});
  }

  getNoteById(noteId: string): Observable<NoteViewModel> {
    return this.appService.GetById('Notes', 'GetNoteById', noteId);
  }

  createNote(model: CreateNoteViewModel): Observable<NoteViewModel> {

    const companyData = JSON.parse(
      localStorage.getItem('ChnageCompnayId')
    );

    return this.http.post<NoteViewModel>(
      environment.apiUrl + 'api/Notes/CreateNote',
      model,
      {
        headers: this.buildHeaders()
      }
    );
  }

  updateNote(noteId: string, model: UpdateNoteViewModel): Observable<NoteViewModel> {

    const companyData = JSON.parse(
      localStorage.getItem('ChnageCompnayId')
    );

    return this.http.post<NoteViewModel>(
      environment.apiUrl + `api/Notes/UpdateNote?noteId=${noteId}`,
      model,
      {
        headers: this.buildHeaders()
      }
    );
  }

  softDeleteNote(noteId: string): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}api/Notes/SoftDeleteNote?noteId=${noteId}`,
      {},
      {
        headers: this.buildHeaders()
      }
    );
  }

  permanentDeleteNote(noteId: string): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}api/Notes/PermanentDeleteNote?noteId=${noteId}`,
      {},
      {
        headers: this.buildHeaders()
      }
    );
  }

  deleteAllTrash(): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}api/Notes/DeleteAllTrash`,
      {},
      {
        headers:this.buildHeaders()
      }
    );
  }

  restoreNote(noteId: string): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}api/Notes/RestoreNote?noteId=${noteId}`,
      {},
      {
        headers: this.buildHeaders()
      }
    );
  }

  togglePin(noteId: string): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}api/Notes/TogglePin?noteId=${noteId}`,
      {},
      {
        headers: this.buildHeaders()
      }
    );
  }

  toggleArchive(noteId: string): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}api/Notes/ToggleArchive?noteId=${noteId}`,
      {},
      {
        headers: this.buildHeaders()
      }
    );
  }

  // Search 
  searchNotesGlobal(params: GlobalSearchParams): Observable<SearchNoteResultViewModel[]> {
    return this.appService.Post('Notes', 'SearchNotesGlobal', params);
  }

  // Trash 
  getTrashedNotes(pageNumber: number = 1, pageSize: number = 20): Observable<any> {
    return this.http.get(
      `${environment.apiUrl}api/Notes/GetTrashedNotes?pageNumber=${pageNumber}&pageSize=${pageSize}`,
      {
        headers: this.buildHeaders()
      }
    );
  }

  // Attachments 
  addAttachment(model: AddAttachmentViewModel, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('NoteId', model.NoteId);
    formData.append('FileType', file.type);
    formData.append('file', file, file.name);

    const token = this.appService.getAuthToken();
    const companyData = JSON.parse(localStorage.getItem("ChnageCompnayId"));
    const headers = new HttpHeaders({Authorization: token, CompanyId: companyData ? companyData.Id : ''});

    return this.http.post(`${environment.apiUrl}api/Notes/AddAttachment`,
      formData,
      { headers }
    );
  }

  // Mentions 
  getUnreadMentions(): Observable<MentionViewModel[]> {
     return this.http.get<MentionViewModel[]>(
      `${environment.apiUrl}api/Notes/GetUnreadMentions`,
      {
        headers: this.buildHeaders()
      }
    ).pipe(
      map((data: MentionViewModel[]) => {
        this._unreadMentionCount$.next(data ? data.length : 0);
        return data;
      })
    );
  }

  markMentionNotified(mentionId: number): Observable<any> {
    return this.appService.Post('Notes', 'MarkMentionNotified', { MentionId: mentionId });
  }

  // Subscriptions 
  createSubscription(transactionType: string, transactionId: string): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}api/Notes/CreateSubscription`,
      {
        TransactionType: transactionType,
        TransactionId: transactionId
      },
      {
        headers: this.buildHeaders()
      }
    );
  }

  getSubscribedNotes(transactionType: string, transactionId: string): Observable<NoteViewModel[]> {
    return this.http.get<NoteViewModel[]>(
      `${environment.apiUrl}api/Notes/GetSubscribedNotes?TransactionType=${transactionType}&TransactionId=${transactionId}`,
      {
        headers: this.buildHeaders()
      }
    );
  }

  // Helpers 
  parseTags(tagsString: string): string[] {
    if (!tagsString) return [];
    return tagsString.split(',').map((t) => t.trim()).filter((t) => t.length > 0);
  }

  generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  getAllNotes(params: GlobalNoteFilterParams): Observable<any> {
    let queryParams = [];
    queryParams.push(`filterType=${params.filterType}`);

    if (params.transactionType) {
      queryParams.push(`transactionType=${params.transactionType}`);
    }

    if (params.fromDate) {
      queryParams.push(`fromDate=${params.fromDate}`);
    }

    if (params.toDate) {
      queryParams.push(`toDate=${params.toDate}`);
    }

    if (params.createdBy) {
      queryParams.push(`createdBy=${params.createdBy}`);
    }

    if (params.noteType) {
      queryParams.push(`noteType=${params.noteType}`);
    }
    
    queryParams.push(`pageNumber=${params.pageNumber}`);
    queryParams.push(`pageSize=${params.pageSize}`);

    if (params.searchTerm) queryParams.push(`searchTerm=${encodeURIComponent(params.searchTerm)}`);
    if (params.tagName) queryParams.push(`tagName=${encodeURIComponent(params.tagName)}`);

    const queryString = queryParams.length > 0 ? '?' + queryParams.join('&') : '';
    const url = 'GetAllNotes' + queryString;

    const companyData = JSON.parse(localStorage.getItem('ChnageCompnayId'));

    return this.http.get(`${environment.apiUrl}api/Notes/${url}`,
      {
        headers: this.buildHeaders()
      }
    ).pipe(
      map((res: any) => {
        if (res && res.notes && Array.isArray(res.notes)){
          res.notes = res.notes.map((note: any) => {
            if (note.AttachmentsJson && typeof note.AttachmentsJson === 'string') {
              try {
                note.Attachments = JSON.parse(note.AttachmentsJson);
              } catch (e) {
                note.Attachments = [];
              }
            }else {
              note.Attachments = [];
            }

            if (note.MentionsJson && typeof note.MentionsJson === 'string') {
              try {
                note.Mentions = JSON.parse(note.MentionsJson);
              } catch (e) {
                note.Mentions = [];
              }
            } else {
              note.Mentions = []
            }

            return note;
          });
        }
        return res;
      })
    );
  }

  getGlobalNotesFilterData() : Observable<any> {
    console.log('calling GetGlobalNoteFilterDate API')
    return this.http.get(
      `${environment.apiUrl}api/Notes/GetGlobalNotesFilterData`,
      {
        headers: this.buildHeaders()
      }
    );
  }

  getGlobalNoteCounts(): Observable<GlobalNoteCounts> {
    const companyData = JSON.parse(
      localStorage.getItem('ChnageCompnayId')
    );

    return this.http.get<GlobalNoteCounts>(
      `${environment.apiUrl}api/Notes/GetGlobalNoteCounts`,
      {
        headers: this.buildHeaders()
      }
    );
  }
  

  getUserTags(): Observable<UserTag[]> {
    const companyData = JSON.parse(
      localStorage.getItem('ChnageCompnayId')
    );

    return this.http.get<UserTag[]>(
      `${environment.apiUrl}api/Notes/GetUserTags`,
      {
        headers: this.buildHeaders()
      }
    );
  }

  getTrashedNotesGlobal(pageNumber: number = 1, pageSize: number = 20): Observable<any> {
    const companyData = JSON.parse(
      localStorage.getItem('ChnageCompnayId')
    );

    return this.http.get(
      `${environment.apiUrl}api/Notes/GetTrashedNotesGlobal?pageNumber=${pageNumber}&pageSize=${pageSize}`,
      {
        headers: this.buildHeaders()
      }
    );
  }

  restoreNoteGlobal(noteId: string): Observable<any> {
    const companyData = JSON.parse(
      localStorage.getItem('ChnageCompnayId')
    );

    return this.http.post(
      `${environment.apiUrl}api/Notes/RestoreNoteGlobal?noteId=${noteId}`,
      {},
      {
        headers: this.buildHeaders()
      }
    );
  }
}